import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Box, Text, Button } from "grommet"
import {
    Code, Cpu, Memory, Host, DriveCage, Connectivity,
    Install, Launch, Restaurant,
    Currency, CaretDown, Services, StatusGood, StatusCritical,
    Book, User, Database, Accessibility
} from 'grommet-icons'

import TimeAgo from 'react-timeago'
import * as moment from "moment-timezone"

import { useVersionHistory } from '../../../utils/version_history'

import { getLocalDateTimeWithTimeZoneFromEpochTime } from '../../../utils/localdatetime'
import UserContext from "../../../context/UserContext";
import TTTip from '../../common/TTTip'

import { gql, useQuery } from '@apollo/client'
import { useBankAlgoNameToMarkets } from '../../../utils/bankalgo'
import { useMarket } from '../../../utils/market'
import { InstanceNotes, NodeNotes } from '../../common/NotesV2'

const FIXAdapterConfMap = {
    "fixadapter-fixinbounddropcopy-node": "fixinbounddropcopy",
    "fixadapter-fixorderrouting-node": "fixorderrouting",
    "fixadapter-fixdropcopy-node": "fixdropcopy"
}

export const TTStat = ({ topContent, middleContent, bottomContent, icon = null, width = "300px", backgroundColor = "light-2" }) => {
    return (
        <Box width={width} direction="column" gap="3px" background={backgroundColor} pad="xsmall" round="xsmall" margin={{ top: "5px" }}>
            <Box direction="row" gap="3px" align="center">{icon} <Text size="xsmall" color="brand"> {topContent}</Text></Box>
            <Box direction="row" gap="small" align="center">
                <Text size="small" weight="bold">{middleContent}</Text>
                <Text size="xsmall">{bottomContent}</Text>
            </Box>
        </Box>
    )
}


export const AWSStats = ({ awsdata }) => {
    const token = useContext(UserContext);

    const parseCodeSize = (codesize) => {
        function round(num, decimalPlaces = 0) {
            var p = Math.pow(10, decimalPlaces);
            var n = (num * p) * (1 + Number.EPSILON);
            return Math.round(n) / p;
        }

        if (codesize > 1024 * 1024 * 1024) {
            return round(codesize / 1024 / 1024 / 1024, 2) + " GB"
        }
        else if (codesize > 1024 * 1024) {
            return round(codesize / 1024 / 1024, 2) + " MB"
        } else {
            return codesize + " B"
        }
    }
    return (
        <Box gap="small" fill="horizontal">
            <Box direction="row" gap="xsmall" fill="horizontal" wrap={true}>

                {awsdata && awsdata.AwsApplication === "Ec2" &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={"architecture: " + awsdata.Architecture}
                        middleContent={awsdata.InstanceType}
                        bottomContent={
                            <Box direction="row" gap="small">
                                <TTTip hoverMessage="CPU">
                                    <Button plain icon={<Cpu size="16px" color="brand" />} label={<Text size="xsmall">{awsdata.CpuOptions.CoreCount + " CPU"}</Text>} />
                                </TTTip>
                            </Box>
                        }
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Ec2" &&
                    awsdata.LaunchTime &&
                    <TTStat
                        icon={<Install size="16px" color="brand" />}
                        topContent="launched"
                        middleContent={<TimeAgo date={awsdata.LaunchTime} />}
                        bottomContent={moment(awsdata.LaunchTime).tz(token.timezone).format('MMMM DD, YYYY h:mm:ss A zz')}
                    />
                }

                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Redshift" &&
                    awsdata.NodeType &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={"node"}
                        middleContent={awsdata.NodeType}
                        bottomContent={"Cluster Version " + awsdata.ClusterVersion}
                    />
                }

                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticbeanstalk" &&
                    awsdata.SolutionStackName &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={"stack"}
                        middleContent={awsdata.SolutionStackName}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticbeanstalk" &&
                    awsdata.DateUpdated &&
                    <TTStat
                        icon={<Install size="16px" color="brand" />}
                        topContent="installed"
                        middleContent={<TimeAgo date={awsdata.DateUpdated} />}
                        bottomContent={moment(awsdata.DateUpdated).tz(token.timezone).format('MMMM DD, YYYY h:mm:ss A zz')}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticbeanstalk" &&
                    awsdata.VersionLabel &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent="version"
                        middleContent={awsdata.VersionLabel}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticbeanstalk" &&
                    awsdata.AutoScalingGroups && awsdata.AutoScalingGroups.map(eachrow => {
                        return (
                            <TTStat
                                key={eachrow.TargetGroupARNs}
                                icon={<Services size="16px" color="brand" />}
                                topContent={"Autoscaling Config"}
                                middleContent={
                                    <Box direction="row" gap="small">
                                        <Text size="xsmall">Desired:{eachrow.DesiredCapacity}  Min:{eachrow.MinSize}  Max:{eachrow.MaxSize} </Text>
                                    </Box>
                                }
                                bottomContent={
                                    <Box direction="row" gap="small">
                                        <Text size="xsmall">Cooldown:{eachrow.DefaultCooldown}</Text>
                                    </Box>
                                }
                            />
                        )
                    })
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticache" &&
                    awsdata.Engine &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent="engine"
                        middleContent={awsdata.EngineVersion}
                        bottomContent={awsdata.Engine}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticache" &&
                    awsdata.CacheNodeType &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent="node"
                        middleContent={awsdata.CacheNodeType}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Elasticache" &&
                    awsdata.NumCacheNodes &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent="# of nodes"
                        middleContent={awsdata.NumCacheNodes}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Rds" &&
                    awsdata.EngineVersion &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={"engine"}
                        middleContent={awsdata.EngineVersion}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Rds" &&
                    awsdata.DBInstanceClass &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent={"DBInstanceClass"}
                        middleContent={awsdata.DBInstanceClass}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Lambda" &&
                    awsdata.FunctionName &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent={"service"}
                        middleContent={awsdata.FunctionName}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Lambda" &&
                    awsdata.CodeSize &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent={"code size"}
                        middleContent={parseCodeSize(awsdata.CodeSize)}
                    />
                }
                {awsdata && awsdata.AwsApplication === "Lambda" &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={"Runtime: " + awsdata.Runtime}
                        middleContent={
                            <Box direction="row" gap="small">
                                <TTTip hoverMessage="Memory">
                                    <Button plain icon={<Memory size="16px" color="brand" />} label={<Text size="xsmall">{awsdata.MemorySize + " MB"}</Text>} />
                                </TTTip>
                            </Box>
                        }
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Lambda" &&
                    awsdata.LastModified &&
                    <TTStat
                        icon={<Install size="16px" color="brand" />}
                        topContent="installed"
                        middleContent={<TimeAgo date={awsdata.LastModified} />}
                        bottomContent={moment(awsdata.LastModified).tz(token.timezone).format('MMMM DD, YYYY h:mm:ss A zz')}
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Lambda" &&
                    awsdata.MemorySize && awsdata.Architectures && awsdata.Architectures.length > 0 &&
                    awsdata.EphemeralStorage && awsdata.EphemeralStorage.Size &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={"architecture: " + awsdata.Architectures[0]}
                        middleContent={awsdata.Runtime}
                        bottomContent={
                            <Box direction="row" gap="small">
                                <TTTip hoverMessage="Memory">
                                    <Button plain icon={<Memory size="16px" color="brand" />} label={<Text size="xsmall">{awsdata.MemorySize + " MB"}</Text>} />
                                </TTTip>

                                <TTTip hoverMessage="Ephemeral Storage">
                                    <Button plain icon={<DriveCage size="16px" color="brand" />} label={<Text size="xsmall">{awsdata.EphemeralStorage.Size + " MB"}</Text>} />
                                </TTTip>
                            </Box>



                        }
                    />
                }
                {awsdata && awsdata.AwsApplication && awsdata.AwsApplication === "Lambda" &&
                    awsdata.Version &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent="version"
                        middleContent={awsdata.Version}
                    />
                }
                {awsdata && awsdata.AwsApplication === "Ecs" &&
                    <TTStat
                        icon={<Services size="16px" color="brand" />}
                        topContent="tasks"
                        middleContent={`${awsdata.runningCount ?? 0}/${awsdata.desiredCount ?? 0} running`}
                        bottomContent={awsdata.pendingCount ? `${awsdata.pendingCount} pending` : null}
                    />
                }
                {awsdata && awsdata.AwsApplication === "Ecs" &&
                    awsdata.launchType &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent="launch type"
                        middleContent={awsdata.launchType}
                    />
                }
                {awsdata && awsdata.AwsApplication === "Ecs" &&
                    awsdata.taskDefinition &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent="task definition"
                        middleContent={awsdata.taskDefinition.split("/").pop()}
                    />
                }
                {awsdata && awsdata.AwsApplication === "Ecs" &&
                    awsdata.createdAt &&
                    <TTStat
                        icon={<Install size="16px" color="brand" />}
                        topContent="created"
                        middleContent={<TimeAgo date={awsdata.createdAt} />}
                        bottomContent={moment(awsdata.createdAt).tz(token.timezone).format('MMMM DD, YYYY h:mm:ss A zz')}
                    />
                }
                {awsdata && awsdata.AwsApplication === "Ecs" &&
                    awsdata.Tasks && awsdata.Tasks.map((task, index) => {
                        const unhealthy = task.healthStatus === "UNHEALTHY"
                        const taskId = (task.taskArn || "").split("/").pop() || `#${index + 1}`
                        return (
                            <TTStat
                                key={task.taskArn || `task-${index}`}
                                icon={unhealthy ? <StatusCritical size="16px" color="status-critical" /> : <StatusGood size="16px" color="status-ok" />}
                                topContent={"task " + taskId}
                                middleContent={task.lastStatus}
                                bottomContent={
                                    <Text size="xsmall">
                                        {task.healthStatus} {(task.containers || []).map(c => `${c.name}:${c.lastStatus}`).join(" ")}
                                    </Text>
                                }
                            />
                        )
                    })
                }
            </Box>
        </Box>
    )
}


export const CombinedStats = ({
    env,
    service_name,
    ip,
    zk_start_time,
    node,
    serviceGroup,
    marketId,
    ocCluster = "",
    instanceId = "",
    bouncerCustomData = {},
    trade_interface_ip = ""
}) => {
    const [startAppVersion, setStartAppVersion] = useState("")
    const [startAppTime, setStartAppTime] = useState("")

    const versionHistory = useVersionHistory([node])

    const token = useContext(UserContext);

    const marketLookup = useMarket()

    // For handling bouncer init_state_data
    const { accountInit, pdsReconcile, ttusReconcile } = useMemo(() => {
        if (serviceGroup === "bouncer") {
            const initStateData = bouncerCustomData?.init_state_data || {};
            return {
                accountInit: initStateData.account_init_complete ?? true,
                pdsReconcile: initStateData.pds_reconcile_complete ?? true,
                ttusReconcile: initStateData.ttus_reconcile_complete ?? true
            };
        }
        return { accountInit: true, pdsReconcile: true, ttusReconcile: true };
    }, [serviceGroup, bouncerCustomData]);

    useEffect(() => {
        // Parse Start App String and Time
        if (service_name && node && versionHistory[node] && versionHistory[node][service_name]) {
            let installedEpoch = versionHistory[node][service_name].installed_epoch
            installedEpoch = moment.unix(installedEpoch).format('YYYY-MM-DD HH:mm:ss.SSS ZZ')
            installedEpoch = moment(installedEpoch).tz(token.timezone).format('YYYY-MM-DD HH:mm:ss.SSS ZZ')
            setStartAppTime(installedEpoch)
            setStartAppVersion(versionHistory[node][service_name].version)
        }

    }, [node, service_name, versionHistory, token.timezone])

    // Machine Data
    const CHEF_DETAILS = gql`
    query ($nodeName: String!) {
        node(where: {name: {_eq: $nodeName}}) {
          serviceGroup_nodes {
            tags: chef(path: "tags")
            machine: chef(path: "machine")
            fixinbounddropcopy: chef(path: "inbound_drop_copy")
            fixdropcopy: chef(path: "fixdropcopy")
            fixorderrouting: chef(path: "fixorderrouting")
            vendor_algo_child_router_mode: chef(path: "vendor_algo_child_router_mode")
            primary_markets: chef(path: "primary_markets")
            bankalgo_type: chef(path: "bankalgo_type")
            datacenter: chef(path: "datacenter")
          }
          name
        }
    }
    `
    const [subdata, setData] = useState(null)
    const { data, loading } = useQuery(CHEF_DETAILS, {
        variables: { nodeName: node }, pollInterval: 1000 * 60
    })


    useEffect(() => {
        if (!loading && data &&
            data.node &&
            data.node.length > 0 &&
            data.node[0] &&
            data.node[0].serviceGroup_nodes.length > 0 &&
            data.node[0].serviceGroup_nodes[0].tags) {
            setData(data)
        }
    }, [loading, data])

    const [baMarkets, setBAMarkets] = useState([])
    const baMap = useBankAlgoNameToMarkets(serviceGroup)

    useEffect(() => {
        if (serviceGroup === "fixgworders" && !loading && data &&
            data.node && data.node.length > 0 &&
            data.node[0] && data.node[0].serviceGroup_nodes.length > 0 &&
            data.node[0].serviceGroup_nodes[0].bankalgo_type &&
            baMap && baMap[env]
        ) {
            let bankalgoType = data.node[0].serviceGroup_nodes[0].bankalgo_type
            if (baMap[env][bankalgoType]) {
                let bankalgoMarkets = baMap[env][bankalgoType]
                setBAMarkets(bankalgoMarkets)
            }

        }

    }, [env, serviceGroup, baMap, loading, data])


    const show_markets = (['ase', 'algoserver_debug', 'algoserver_exec', 'bookie', 'bouncer', 'imbookie', 'juno_up', 'kafka_uploader', 'ledger_up', 'stager', 'TTINT'].includes(serviceGroup))
    let markets = ""
    let markets_count = 0
    if (subdata && subdata.node[0].serviceGroup_nodes[0].primary_markets) {
        markets_count = subdata.node[0].serviceGroup_nodes[0].primary_markets.length
        markets = subdata.node[0].serviceGroup_nodes[0].primary_markets.slice().sort().join(', ')
    }

    // Bookie Metadata
    const BOOKIE_METADATA = gql`
    query($env: String!) {
        BookieMetadata(where: { env: { _eq: $env } }) {
          markets_added
          markets_removed
          markets_bypass_sync
          connections_bypass_sync
          datacenter
        }
      }
    `
    const [addedMarkets, setAddedMarkets] = useState([])
    const [removedMarkets, setRemovedMarkets] = useState([])
    const [bypassSyncMarkets, setBypassSyncMarkets] = useState([])
    const [bypassSyncConnections, setBypassSyncConnections] = useState([])

    const bkmetadata = useQuery(BOOKIE_METADATA, {
        variables: { env: env }, pollInterval: 1000 * 60
    })

    useEffect(() => {
        if (!loading && data &&
            data.node &&
            data.node.length > 0 &&
            data.node[0] &&
            data.node[0].serviceGroup_nodes.length > 0 &&
            data.node[0].serviceGroup_nodes[0].datacenter &&
            marketLookup &&
            !bkmetadata.loading && bkmetadata.data && bkmetadata.data.BookieMetadata) {
            let datacenter = data.node[0].serviceGroup_nodes[0].datacenter
            bkmetadata.data.BookieMetadata.forEach(row => {
                if (row.datacenter === datacenter) {
                    let tmpAddedMarkets = []
                    row.markets_added.forEach(mktId => {
                        if (marketLookup[mktId]) {
                            tmpAddedMarkets.push(marketLookup[mktId].name)
                        }
                    })
                    setAddedMarkets(tmpAddedMarkets)

                    let tmpRemovedMarkets = []
                    row.markets_removed.forEach(mktId => {
                        if (marketLookup[mktId]) {
                            tmpRemovedMarkets.push(marketLookup[mktId].name)
                        }
                    })
                    setRemovedMarkets(tmpRemovedMarkets)

                    let tmpBypassSyncMarkets = []
                    if (row.markets_bypass_sync) {
                        row.markets_bypass_sync.forEach(mktId => {
                            if (marketLookup[mktId]) {
                                tmpBypassSyncMarkets.push(marketLookup[mktId].name)
                            }
                        })
                        setBypassSyncMarkets(tmpBypassSyncMarkets)
                    }

                    if (row.connections_bypass_sync) {
                        setBypassSyncConnections(row.connections_bypass_sync)
                    }
                }
            })
        }

    }, [bkmetadata.data, bkmetadata.loading, data, loading, marketLookup])


    return (
        <Box gap="small" fill="horizontal">
            <Box direction="row" gap="xsmall" fill="horizontal" wrap={true}>
                {startAppVersion &&
                    <TTStat
                        icon={<Code size="16px" color="brand" />}
                        topContent={service_name}
                        middleContent={startAppVersion}
                        bottomContent="version"
                    />
                }
                {startAppTime &&
                    <TTStat
                        icon={<Install size="16px" color="brand" />}
                        topContent="installed"
                        middleContent={<TimeAgo date={startAppTime} />}
                        bottomContent={moment(startAppTime).tz(token.timezone).format('MMMM DD, YYYY h:mm:ss A zz')}
                    />
                }
                {zk_start_time &&
                    <TTStat
                        icon={<Launch size="16px" color="brand" />}
                        topContent="started"
                        middleContent={<TimeAgo date={getLocalDateTimeWithTimeZoneFromEpochTime(token.timezone, zk_start_time)} />}
                        bottomContent={getLocalDateTimeWithTimeZoneFromEpochTime(token.timezone, zk_start_time)}
                    />
                }
                {subdata &&
                    <TTStat
                        icon={<Host size="16px" color="brand" />}
                        topContent={subdata.node[0].serviceGroup_nodes[0].machine.system_manufacturer + " " + subdata.node[0].serviceGroup_nodes[0].machine.system_product_name}
                        middleContent={subdata.node[0].serviceGroup_nodes[0].machine.os + " " + subdata.node[0].serviceGroup_nodes[0].machine.version}
                        bottomContent={
                            <Box direction="row" gap="small">
                                {subdata.node[0].serviceGroup_nodes[0].machine.drive_type === 'SSD' &&
                                    <TTTip hoverMessage="SSD">
                                        <Button plain icon={<DriveCage size="16px" />} label={<Text size="xsmall" weight="bold">SSD</Text>} />
                                    </TTTip>
                                }
                                <TTTip hoverMessage="CPUs">
                                    <Button plain icon={<Cpu size="16px" color="brand" />} label={<Text size="xsmall">{subdata.node[0].serviceGroup_nodes[0].machine.cpu_total}</Text>} />
                                </TTTip>
                                <TTTip hoverMessage="Memory">
                                    <Button plain icon={<Memory size="16px" color="brand" />} label={<Text size="xsmall">{parseMemory(subdata.node[0].serviceGroup_nodes[0].machine.memory_total)}</Text>} />
                                </TTTip>
                            </Box>
                        }
                    />
                }

                {serviceGroup === "bookie" &&
                    <TTStat
                        icon={<Currency size="16px" color="brand" />}
                        topContent={"DR Markets"}
                        middleContent={
                            <Box direction="column" gap="small">
                                <Box direction="row" gap="small">
                                    <Button plain icon={
                                        addedMarkets.length === 0 ?
                                            <StatusGood size="16px" color="ttok" /> :
                                            <StatusCritical size="16px" color="ttdown" />
                                    } label={<Text size="xsmall">Added Markets</Text>} />
                                    <Box direction="row" gap="small">
                                        <Text size="xsmall">{addedMarkets.join(", ")}</Text>
                                    </Box>
                                </Box>

                                <Box direction="row" gap="small">
                                    <Button plain icon={
                                        removedMarkets.length === 0 ?
                                            <StatusGood size="16px" color="ttok" /> :
                                            <StatusCritical size="16px" color="ttdown" />
                                    } label={<Text size="xsmall">Removed Markets</Text>} />
                                    <Box direction="row" gap="small">
                                        <Text size="xsmall">{removedMarkets.join(", ")}</Text>
                                    </Box>
                                </Box>
                            </Box>
                        }
                    />
                }

                {serviceGroup === "bookie" &&
                    <TTStat
                        icon={<Accessibility size="16px" color="brand" />}
                        topContent={"Bypass Sync"}
                        middleContent={
                            <Box direction="column" gap="small">
                                <Box direction="row" gap="small">
                                    <Button plain icon={
                                        bypassSyncMarkets.length === 0 ?
                                            <StatusGood size="16px" color="ttok" /> :
                                            <StatusCritical size="16px" color="ttdown" />
                                    } label={<Text size="xsmall">Bypass Sync Markets</Text>} />
                                    <Box direction="row" gap="small">
                                        <Text size="xsmall">{bypassSyncMarkets.join(", ")}</Text>
                                    </Box>
                                </Box>

                                <Box direction="row" gap="small">
                                    <Button plain icon={
                                        bypassSyncConnections.length === 0 ?
                                            <StatusGood size="16px" color="ttok" /> :
                                            <StatusCritical size="16px" color="ttdown" />
                                    } label={<Text size="xsmall">Bypass Sync Connections</Text>} />
                                    <Box direction="row" gap="small">
                                        <Text size="xsmall">{bypassSyncConnections.join(", ")}</Text>
                                    </Box>
                                </Box>
                            </Box>
                        }
                    />
                }

                {(serviceGroup === "bouncer") &&
                    <TTStat
                        icon={<Book size="16px" color="brand" />}
                        topContent={"Account Initialisation"}
                        middleContent={<Text size="small" color={accountInit ? "green" : "red"}>{accountInit ? "Completed" : "Not Completed"}</Text>}
                        bottomContent={""}
                    />
                }

                {(serviceGroup === "bouncer") &&
                    <TTStat
                        icon={<Database size="16px" color="brand" />}
                        topContent={"PDS Reconciliation"}
                        middleContent={<Text size="small" color={pdsReconcile ? "green" : "red"}>{pdsReconcile ? "Completed" : "Not Completed"}</Text>}
                        bottomContent={""}
                    />
                }

                {(serviceGroup === "bouncer") &&
                    <TTStat
                        icon={<User size="16px" color="brand" />}
                        topContent={"TTUS Reconciliation"}
                        middleContent={<Text size="small" color={ttusReconcile ? "green" : "red"}>{ttusReconcile ? "Completed" : "Not Completed"}</Text>}
                        bottomContent={""}
                    />
                }

                {(show_markets && markets_count <= 20 &&
                    <TTStat
                        icon={<Currency size="16px" color="brand" />}
                        topContent={"Markets"}
                        middleContent={markets}
                        bottomContent={""}
                    />
                )}
                {(show_markets && markets_count > 20 &&
                    <TTStat
                        icon={<Currency size="16px" color="brand" />}
                        topContent={"Markets"}
                        middleContent={
                            <Box align="start" height="xsmall" overflow="scroll">
                                <Text size="12px">{markets}</Text>
                            </Box>
                        }
                        bottomContent={<CaretDown size="16px" />}
                    />
                )}

                {(baMarkets && serviceGroup === "fixgworders" &&
                    <TTStat
                        icon={<Currency size="12px" color="brand" />}
                        topContent={"FOG Markets"}
                        middleContent={baMarkets.join(", ")}
                        bottomContent={""}
                    />
                )}

                {subdata &&
                    subdata.node &&
                    subdata.node.length > 0 &&
                    subdata.node[0] &&
                    subdata.node[0].serviceGroup_nodes.length > 0 &&
                    subdata.node[0].serviceGroup_nodes[0].tags &&
                    subdata.node[0].serviceGroup_nodes[0].tags.length > 0 &&
                    <TTStat
                        width="300px"
                        icon={<Restaurant size="16px" color="brand" />}
                        topContent={"Chef Tags"}
                        middleContent={""}
                        bottomContent={
                            <Box direction="column" gap="small">
                                {subdata.node[0].serviceGroup_nodes[0].tags.map(k =>
                                    <Text key={k} wordBreak="break-word" color={(k.includes("chefdisabled") || k.includes("on_restart_disabled") || k.includes("ttservice_notifications_disabled")) ? "ttdown" : "brand"} size="xsmall" margin="none" textAlign="start" weight="bold">{k}</Text>
                                )
                                }
                            </Box>
                        }
                    />
                }

                {subdata && (
                    serviceGroup === "fixadapter-fixorderrouting-node" ||
                    serviceGroup === "fixadapter-fixdropcopy-node" ||
                    serviceGroup === "fixadapter-fixinbounddropcopy-node") &&
                    subdata.node && subdata.node.length > 0 && subdata.node[0] &&
                    subdata.node[0].serviceGroup_nodes.length > 0 &&
                    subdata.node[0].serviceGroup_nodes[0][FIXAdapterConfMap[serviceGroup]] &&
                    <TTStat
                        width="300px"
                        icon={<Connectivity size="16px" color="brand" />}
                        topContent={"FIX Initiator"}
                        middleContent={""}
                        bottomContent={
                            <Box direction="column" gap="xsmall">
                                {Object.keys(subdata.node[0].serviceGroup_nodes[0][FIXAdapterConfMap[serviceGroup]]).map(k =>
                                    <Text key={k} wordBreak="break-word" size="xsmall" margin="none" textAlign="start" weight="bold">{
                                        k + ' = ' + subdata.node[0].serviceGroup_nodes[0][FIXAdapterConfMap[serviceGroup]][k]
                                    }</Text>
                                )
                                }
                            </Box>
                        }
                    />
                }

                {subdata && serviceGroup === "fixadapter-fixorderrouting-node" &&
                    subdata.node && subdata.node.length > 0 && subdata.node[0] &&
                    subdata.node[0].serviceGroup_nodes.length > 0 &&
                    subdata.node[0].serviceGroup_nodes[0]["vendor_algo_child_router_mode"] &&
                    <TTStat
                        width="300px"
                        icon={<Connectivity size="16px" color="brand" />}
                        topContent={"RCM-X"}
                        middleContent={""}
                        bottomContent={
                            <Box direction="column" gap="xsmall">
                                <Text key="rcm-x" wordBreak="break-word" size="xsmall" margin="none" textAlign="start" weight="bold">
                                    {"vendor_algo_child_router_mode = " + subdata.node[0].serviceGroup_nodes[0]["vendor_algo_child_router_mode"]}
                                </Text>
                            </Box>
                        }
                    />
                }

                {/* V2 Notes */}
                {instanceId &&
                    <InstanceNotes instanceId={instanceId} />
                }
                <NodeNotes nodeName={node} />

            </Box>
        </Box>
    )
}


const parseMemory = (memory_total) => {
    let memory = ''
    memory = memory_total.replace("kB", "") / 1024 / 1024
    return Math.round(memory) + " GB"
}
